import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { InteractionStatus, type IPublicClientApplication } from "@azure/msal-browser";
import { ApiError, setAuthHeaderProvider, UNAUTHORIZED_EVENT } from "../api/client";
import { bootstrapApi, resetBootstrapState } from "../api/bootstrapApi";
import { summitOpsApi } from "../api/summitOpsApi";
import type { Me } from "../api/types";
import { Roles, type Role } from "./roles";
import { ENTRA_NOT_CONFIGURED, fallbackAuthConfig, type AuthMode, type SpaAuthConfig } from "./authConfig";
import { acquireApiToken, acquireGraphToken, currentAccount, decodeJwtClaims, signInRedirect, signOutRedirect } from "./msal";
import { setGraphTokenProvider } from "./graph";
import { runSelfSync } from "../lib/directory/selfSync";

/** O-R56: the API's "access paused" refusal, in its own words, or null for any other error. */
export const ACCESS_PAUSED_TYPE = "https://summit-aviation.com/problems/access-paused";
export function pausedMessage(error: unknown): string | null {
  return error instanceof ApiError && error.status === 403 && error.type === ACCESS_PAUSED_TYPE ? error.message : null;
}

// Auth context, ported from Summit Engineering's shape. Two modes, chosen at RUN time by the API (GET /auth/config):
//  • entra       — Microsoft Entra ID through MSAL: redirect sign-in, a bearer token on every API call, the principal
//                  from /me (the API enforces). Privileged roles also pass the authenticator step-up (Q-O12).
//  • dev-persona — Development only, and only when the API says so: the SPA sends a persona name and the server decides
//                  the roles (X-Dev-Persona). The API refuses this scheme outside Development (StartupGuards).
// Nothing about the person is kept in localStorage: MSAL's cache and the persona name live in sessionStorage, per tab.

export type { AuthMode } from "./authConfig";

const PERSONA_KEY = "summitops.devPersona";
/** The header the API's DevStubAuthenticationHandler reads. */
export const PERSONA_HEADER = "X-Dev-Persona";

export interface DevPersona {
  key: string;
  label: string;
  /** null = signed in from the directory with no Operations role at all. */
  role: Role | null;
  capability: string;
}

/**
 * Display list only — the server owns the persona → role mapping (DevStubAuthenticationHandler.Personas). "anonymous"
 * is not a persona: it is how the not-signed-in path is exercised locally.
 */
export const DEV_PERSONAS: readonly DevPersona[] = [
  { key: "operations-manager", label: "Operations Manager", role: Roles.OperationsManager, capability: "Everything in Operations; approves overrides and publishes baselines" },
  { key: "planner", label: "Planner", role: Roles.Planner, capability: "Priorities, pegs, scenarios, assignments; publishes the baseline" },
  { key: "master-scheduler", label: "Master Scheduler", role: Roles.MasterScheduler, capability: "Everything a Planner does, plus publishing the baseline and approving frozen-zone changes" },
  { key: "supervisor", label: "Shop Supervisor", role: Roles.ShopSupervisor, capability: "Dispatch, downtime, machine status, hotlist notes" },
  { key: "viewer", label: "Operations Viewer", role: Roles.OperationsViewer, capability: "Reads the operations picture; changes nothing" },
  { key: "plant-manager", label: "Plant Manager", role: Roles.PlantManager, capability: "Management reads and final approval of controlled documents" },
  { key: "shipping-manager", label: "Shipping Manager", role: Roles.ShippingManager, capability: "Logistics: what ships today, late deliveries, complete but not shipped" },
  { key: "logistics", label: "Logistics", role: Roles.Logistics, capability: "Shipping staff: the Logistics inbox, shipments and labels (signed)" },
  { key: "purchaser", label: "Purchasing", role: Roles.Purchasing, capability: "Purchasers: check each work-order router, approve and print it, track outside-processing requests" },
  { key: "purchasing-manager", label: "Purchasing Manager", role: Roles.PurchasingManager, capability: "Purchasing: outside-processing requests, shortages, late purchase orders" },
  { key: "quality", label: "Quality", role: Roles.Quality, capability: "QA/QC read-only: compliance, audit log, chain of custody" },
  { key: "executive", label: "Executive (read-only)", role: Roles.Executive, capability: "Leadership: reads the important things, changes nothing" },
  { key: "admin", label: "Admin", role: Roles.Admin, capability: "Governed settings, users and roles, AI activity" },
  { key: "wall-display", label: "Wall Display (kiosk)", role: Roles.WallDisplay, capability: "Shop-floor board only — reaches exactly one endpoint" },
  { key: "no-role", label: "No Operations role yet", role: null, capability: "Signed in from the directory with no role — asks for access" },
  { key: "anonymous", label: "Not signed in", role: null, capability: "Nobody is signed in: the sign-in page" }
];

export interface Principal {
  displayName: string;
  userPrincipalName: string | null;
  roles: string[];
  /** The roles the person was given (their title); `roles` carries the expanded permissions (D-O60). */
  assignedRoles?: string[];
  policies: string[];
  authMode: string;
  /** True when this role must pass the authenticator step before any policy admits it (Q-O12 default: yes). */
  needsStepUp: boolean;
  release: string;
  aiEnabled: boolean;
  /** The plant date the API is working to — never the browser's clock. */
  plantDate: string;
}

interface AuthState {
  principal: Principal | null;
  loading: boolean;
  error: string | null;
  /** The persona currently selected in Development, or null. */
  persona: string | null;
  signInAsPersona: (personaKey: string) => Promise<boolean>;
  signOut: () => void;
  /** How this deployment signs people in. */
  mode: AuthMode;
  /** Entra: sign-in is configured, so "Sign in with Microsoft" is available. */
  entraReady: boolean;
  /** Why Microsoft sign-in is unavailable, when it is. */
  entraUnavailableReason: string | null;
  /** Entra: starts the Microsoft redirect sign-in; `returnTo` is the in-app path to land on. */
  signInWithMicrosoft?: (returnTo: string, chooseAccount?: boolean) => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function principalFromMe(me: Me): Principal {
  return {
    displayName: me.displayName,
    userPrincipalName: me.userPrincipalName,
    roles: me.roles,
    assignedRoles: me.assignedRoles,
    policies: me.policies,
    authMode: me.authMode,
    needsStepUp: me.needsStepUp,
    release: me.release,
    aiEnabled: me.aiEnabled,
    plantDate: me.plantDate
  };
}

function readPersona(): string | null {
  try {
    return sessionStorage.getItem(PERSONA_KEY);
  } catch {
    return null;
  }
}

/** The auth provider for the run-time mode (from GET /auth/config, main.tsx). `msal` is the initialised MSAL instance in Entra mode. */
export function AuthProvider({ children, config, msal }: { children: ReactNode; config?: SpaAuthConfig; msal?: IPublicClientApplication | null }) {
  const effective = config ?? fallbackAuthConfig();
  if (effective.mode === "entra") {
    if (!msal || !effective.configured) return <EntraUnavailableProvider reason={effective.unavailableReason ?? ENTRA_NOT_CONFIGURED}>{children}</EntraUnavailableProvider>;
    return (
      <MsalProvider instance={msal}>
        <EntraAuthProvider config={effective}>{children}</EntraAuthProvider>
      </MsalProvider>
    );
  }
  return <DevPersonaAuthProvider>{children}</DevPersonaAuthProvider>;
}

function DevPersonaAuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [persona, setPersona] = useState<string | null>(() => readPersona());
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [loading, setLoading] = useState<boolean>(persona !== null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAuthHeaderProvider((): Record<string, string> => (persona ? { [PERSONA_HEADER]: persona } : {}));
  }, [persona]);

  const resolve = useCallback(async (personaKey: string): Promise<Principal> => {
    setAuthHeaderProvider(() => ({ [PERSONA_HEADER]: personaKey }));
    const known = DEV_PERSONAS.find((p) => p.key === personaKey);
    if (known?.role === Roles.WallDisplay) {
      // GET /me needs an Operations principal, and the kiosk is deliberately not one: it reaches exactly one board
      // endpoint. So the board principal is built here rather than asked for.
      return {
        displayName: "Shop floor display",
        userPrincipalName: null,
        roles: [Roles.WallDisplay],
        policies: ["WallDisplayAccess"],
        authMode: "DevStub",
        needsStepUp: false,
        release: "—",
        aiEnabled: false,
        plantDate: ""
      };
    }
    return principalFromMe(await bootstrapApi.resolveMeWithBootstrap(queryClient, summitOpsApi.getMe));
  }, [queryClient]);

  useEffect(() => {
    if (!persona) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    resolve(persona)
      .then((p) => !cancelled && setPrincipal(p))
      .catch(() => {
        if (cancelled) return;
        sessionStorage.removeItem(PERSONA_KEY);
        setPersona(null);
        setPrincipal(null);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [persona, resolve]);

  const signInAsPersona = useCallback(
    async (personaKey: string) => {
      setError(null);
      try {
        const p = await resolve(personaKey);
        sessionStorage.setItem(PERSONA_KEY, personaKey);
        queryClient.clear();
        setPrincipal(p);
        setPersona(personaKey);
        return true;
      } catch (e) {
        setAuthHeaderProvider(() => ({}));
        setError(pausedMessage(e) ?? (e instanceof ApiError ? e.title : "Sign-in failed."));
        return false;
      }
    },
    [queryClient, resolve]
  );

  const signOut = useCallback(() => {
    sessionStorage.removeItem(PERSONA_KEY);
    setAuthHeaderProvider(() => ({}));
    resetBootstrapState();
    queryClient.clear();
    setPersona(null);
    setPrincipal(null);
  }, [queryClient]);

  useEffect(() => {
    const onUnauthorized = () => signOut();
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [signOut]);

  const refresh = useCallback(async () => {
    if (!persona) return;
    try {
      setPrincipal(await resolve(persona));
    } catch {
      /* keep the current principal; the next call will say what is wrong */
    }
  }, [persona, resolve]);

  const value = useMemo<AuthState>(
    () => ({ principal, loading, error, persona, signInAsPersona, signOut, mode: "dev-persona", entraReady: false, entraUnavailableReason: null, refresh }),
    [principal, loading, error, persona, signInAsPersona, signOut, refresh]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Entra mode with no usable registration yet: nobody can sign in, and the persona sign-in is never offered instead. */
function EntraUnavailableProvider({ children, reason }: { children: ReactNode; reason: string }) {
  const value = useMemo<AuthState>(
    () => ({
      principal: null,
      loading: false,
      error: reason,
      persona: null,
      signInAsPersona: async () => false,
      signOut: () => undefined,
      mode: "entra",
      entraReady: false,
      entraUnavailableReason: reason,
      refresh: async () => undefined
    }),
    [reason]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}

const ENTRA_SIGN_IN_FAILED = "Microsoft sign-in could not get access to Summit Operations. Sign in again.";

/**
 * Microsoft Entra ID (ported from Summit Engineering's EntraAuthProvider): MSAL redirect sign-in, a bearer token for the
 * API scope on every call, and the principal from GET /me — effective roles (Entra app roles plus in-app grants) and
 * whether this sign-in still needs the authenticator step-up. The API enforces everything; this only decides what is shown.
 */
function EntraAuthProvider({ config, children }: { config: SpaAuthConfig; children: ReactNode }) {
  const queryClient = useQueryClient();
  const { instance, accounts, inProgress } = useMsal();
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const account = currentAccount(instance) ?? accounts[0] ?? null;
  const accountId = account?.homeAccountId ?? null;
  const loadedAccount = useRef<string | null>(null);

  // Every API call carries a bearer token for the API scope.
  useEffect(() => {
    setAuthHeaderProvider(async (): Promise<Record<string, string>> => {
      const token = await acquireApiToken(instance, config);
      return token ? { Authorization: `Bearer ${token}` } : {};
    });
    return () => setAuthHeaderProvider(() => ({}));
  }, [instance, config]);

  // O-R56: Microsoft Graph tokens for the person's own profile (User.Read) and an Admin's refresh of names and photos
  // (User.ReadBasic.All), separate from the API token; silent unless a click asks.
  useEffect(() => {
    setGraphTokenProvider((scope, mode) => acquireGraphToken(instance, scope, mode));
    return () => setGraphTokenProvider(null);
  }, [instance]);

  // O-R56: after sign-in, the person's own Microsoft details and photo, at most once a day; silent and non-blocking.
  useEffect(() => {
    const upn = principal?.userPrincipalName;
    if (!upn) return;
    void runSelfSync(upn).then((applied) => {
      if (applied) void queryClient.invalidateQueries({ queryKey: ["header", "photo"] });
    });
  }, [principal?.userPrincipalName, queryClient]);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    const signedIn = currentAccount(instance);
    if (!signedIn) {
      loadedAccount.current = null;
      setPrincipal(null);
      setLoading(false);
      return;
    }
    if (loadedAccount.current === signedIn.homeAccountId) return;
    if (!instance.getActiveAccount()) instance.setActiveAccount(signedIn);
    let cancelled = false;
    setLoading(true);
    acquireApiToken(instance, config)
      .then(async (token) => {
        if (cancelled || !token) return;
        const claims = decodeJwtClaims(token);
        const tokenRoles = Array.isArray(claims.roles) ? claims.roles.filter((r): r is string => typeof r === "string") : [];
        let next: Principal;
        if (tokenRoles.length === 1 && tokenRoles[0] === Roles.WallDisplay) {
          // The kiosk principal cannot call /me — it reaches exactly one board endpoint.
          next = {
            displayName: typeof claims.name === "string" ? claims.name : "Shop floor display",
            userPrincipalName: null,
            roles: [Roles.WallDisplay],
            policies: ["WallDisplayAccess"],
            authMode: "Bearer",
            needsStepUp: false,
            release: "—",
            aiEnabled: false,
            plantDate: ""
          };
        } else {
          next = principalFromMe(await bootstrapApi.resolveMeWithBootstrap(queryClient, summitOpsApi.getMe));
        }
        if (cancelled) return;
        setPrincipal(next);
        loadedAccount.current = signedIn.homeAccountId;
        setError(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setPrincipal(null);
        setError(pausedMessage(e) ?? ENTRA_SIGN_IN_FAILED);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [instance, config, inProgress, accountId]);

  const refresh = useCallback(async () => {
    try {
      setPrincipal(principalFromMe(await summitOpsApi.getMe()));
    } catch {
      /* keep the current principal */
    }
  }, []);

  const signInWithMicrosoft = useCallback(
    async (returnTo: string, chooseAccount = true) => {
      setError(null);
      try {
        await signInRedirect(instance, config, returnTo, chooseAccount);
      } catch {
        setError("Microsoft sign-in could not start. Try again.");
      }
    },
    [instance, config]
  );

  const signOut = useCallback(() => {
    loadedAccount.current = null;
    setAuthHeaderProvider(() => ({}));
    resetBootstrapState();
    queryClient.clear();
    setPrincipal(null);
    void signOutRedirect(instance).catch(() => undefined);
  }, [instance, queryClient]);

  // A 401 with a Microsoft token means the API did not accept it. Say so; never loop through sign-in automatically.
  useEffect(() => {
    const onUnauthorized = () => setError("The Summit Operations API did not accept your Microsoft sign-in. Sign out and sign in again.");
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      principal,
      loading: loading || (inProgress !== InteractionStatus.None && !principal),
      error,
      persona: null,
      signInAsPersona: async () => false,
      signOut,
      mode: "entra",
      entraReady: true,
      entraUnavailableReason: null,
      signInWithMicrosoft,
      refresh
    }),
    [principal, loading, inProgress, error, signOut, signInWithMicrosoft, refresh]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** The signed-in principal's roles, or none outside a provider — for show/hide decisions only (the API enforces). */
export function useRoles(): readonly string[] {
  return useContext(AuthContext)?.principal?.roles ?? [];
}

export function usePrincipal(): Principal | null {
  return useContext(AuthContext)?.principal ?? null;
}
